import useDocumentEditor from "hooks/useDocumentEditor";
import Annotations from "components/Annotations";
import CertificateApi from "components/CertificateApi";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import PDFVieverHeader from "components/PDFVieverHeader";
import SignatureLabel from "components/SignatureLabel";
import CertContext from "contexts/CertContext";
import LayoutContext from "contexts/LayoutContext";
import PDFContext from "contexts/PDFContext";
import CPluginContext from "contexts/PluginContext";
import { ItemKind } from "dto/IKindItems";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import "react-pdf/dist/Page/TextLayer.css";
import classNames from "classnames";
import { replaceFileExtension } from "utils/replaceFileExtension";
import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";
import { useAuth } from "react-oidc-context";
import moment from "moment";
import SignatureIcon from "assets/icons/signature.svg?react";
import { AutoTextSize } from "auto-text-size";
import CloseIcon from "assets/icons/close.svg?react";
import { Tooltip } from "@mui/material";
import { randomString } from "utils/randomString";
import {
  getDocument,
  GlobalWorkerOptions,
  PDFDocumentProxy,
  TextLayer,
} from "pdfjs-dist";
import "pdfjs-dist/web/pdf_viewer.css";
import { PageViewport } from "pdfjs-dist/types/src/display/display_utils";
import useGetPDFViewer from "hooks/useGetPDFViewer";
import Comments from "components/Comments";
import { IAnnotation } from "dto/IAnnotation";
import Button from "components/Button";
import annotationService from "api/annotationService";
import { User } from "oidc-client-ts";
import usePDF from "hooks/usePDF";
import { useTranslation } from "react-i18next";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

// Worker z naszego bundla, nie z CDN: przetwarza odszyfrowane PDF-y, więc kod spoza naszego wydania czytałby
// każdy otwierany dokument (docs/ebi-plan.md, D9). Wersja zawsze zgodna z zainstalowanym pdfjs-dist.
GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export interface ISignatureField {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zoom: number;
  page: number;
  pwidth: number;
  pheight: number;
}

export interface IViewport {
  rotation: number;
  scale: number;
  width: number;
  height: number;
}

const textLayers = new Map();

const PDFPage2 = ({
  pdfDoc,
  pageNumber,
  scale,
  rotation,
  onVisibleChanged,
  onClick,
  selectionChangeAbortController,
  setSelectionChangeAbortController,
  documentId,
  onAnnotationClick,
  onAnnotationCreated,
  searchString,
  setMatches,
  signatureField,
  updateSignatureField,
  handleRemoveSignatureField,
  user,
}: {
  pdfDoc: PDFDocumentProxy;
  pageNumber: number;
  scale?: number;
  rotation?: number;
  onVisibleChanged?: (page: number, visible: boolean) => void;
  onClick?: (
    page: number,
    e: React.MouseEvent<HTMLElement>,
    viewport: PageViewport,
  ) => void;
  selectionChangeAbortController: AbortController;
  setSelectionChangeAbortController: (v: AbortController) => void;
  documentId: string;
  onAnnotationCreated?: (annotation: IAnnotation) => void;
  onAnnotationClick?: (annotation: IAnnotation) => void;

  activeAnnotation: IAnnotation;
  setActiveAnnotation: (annotation: IAnnotation) => void;
  searchString?: string;
  setMatches?: Dispatch<SetStateAction<any[]>>;
  signatureField?: ISignatureField;
  updateSignatureField?: (id, newProps) => void;
  handleRemoveSignatureField?: () => void;
  user?: User;
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const textLayerRef = useRef(null);
  const divRef = useRef(null);
  const [, setIsVisible] = useState(false);
  const [viewport, setViewport] = useState<PageViewport>(null);

  const {
    PDF,
    setDrawLine,
    setDrawPin,
  } = useGetPDFViewer();

  const bindMouse = (end: HTMLDivElement) => {
    const div = textLayerRef.current;
    textLayerRef.current.addEventListener("mousedown", () => {
      textLayerRef.current.classList.add("selecting");
    });

    // textLayerRef.current.addEventListener("copy", event => {
    //   if (!this.#enablePermissions) {
    //     const selection = document.getSelection();
    //     event.clipboardData.setData(
    //       "text/plain",
    //       removeNullCharacters(normalizeUnicode(selection.toString()))
    //     );
    //   }
    //   stopEvent(event);
    // });

    textLayers.set(div, end);
    enableGlobalSelectionListener();
  };

  const enableGlobalSelectionListener = () => {
    if (selectionChangeAbortController) {
      // document-level event listeners already installed
      return;
    }
    const controller = new AbortController();
    setSelectionChangeAbortController(controller);
    const { signal } = controller;

    const reset = (end, textLayer) => {
      // if (typeof PDFJSDev === "undefined" || !PDFJSDev.test("MOZCENTRAL")) {
      textLayer.append(end);
      end.style.width = "";
      end.style.height = "";
      // }
      textLayer.classList.remove("selecting");
    };

    let isPointerDown = false;
    document.addEventListener(
      "pointerdown",
      () => {
        isPointerDown = true;
      },
      { signal },
    );
    document.addEventListener(
      "pointerup",
      () => {
        isPointerDown = false;
        textLayers.forEach(reset);
      },
      { signal },
    );
    window.addEventListener(
      "blur",
      () => {
        isPointerDown = false;
        textLayers.forEach(reset);
      },
      { signal },
    );
    document.addEventListener(
      "keyup",
      () => {
        if (!isPointerDown) {
          textLayers.forEach(reset);
        }
      },
      { signal },
    );

    //if (typeof PDFJSDev === "undefined" || !PDFJSDev.test("MOZCENTRAL")) {
    // eslint-disable-next-line no-var
    var isFirefox, prevRange;
    //}

    document.addEventListener(
      "selectionchange",
      () => {
        const selection = document.getSelection();
        if (selection.rangeCount === 0) {
          textLayers.forEach(reset);
          return;
        }

        // Even though the spec says that .rangeCount should be 0 or 1, Firefox
        // creates multiple ranges when selecting across multiple pages.
        // Make sure to collect all the .textLayer elements where the selection
        // is happening.
        const activeTextLayers = new Set();
        for (let i = 0; i < selection.rangeCount; i++) {
          const range = selection.getRangeAt(i);
          for (const textLayerDiv of textLayers.keys()) {
            if (
              !activeTextLayers.has(textLayerDiv) &&
              range.intersectsNode(textLayerDiv)
            ) {
              activeTextLayers.add(textLayerDiv);
            }
          }
        }

        for (const [textLayerDiv, endDiv] of textLayers) {
          if (activeTextLayers.has(textLayerDiv)) {
            textLayerDiv.classList.add("selecting");
          } else {
            reset(endDiv, textLayerDiv);
          }
        }

        // if (typeof PDFJSDev !== "undefined" && PDFJSDev.test("MOZCENTRAL")) {
        //   return;
        // }
        // if (typeof PDFJSDev === "undefined" || !PDFJSDev.test("CHROME")) {
        isFirefox ??=
          getComputedStyle(textLayers.values().next().value).getPropertyValue(
            "-moz-user-select",
          ) === "none";

        if (isFirefox) {
          return;
        }
        //}

        // In non-Firefox browsers, when hovering over an empty space (thus,
        // on .endOfContent), the selection will expand to cover all the
        // text between the current selection and .endOfContent. By moving
        // .endOfContent to right after (or before, depending on which side
        // of the selection the user is moving), we limit the selection jump
        // to at most cover the enteirety of the <span> where the selection
        // is being modified.
        const range = selection.getRangeAt(0);
        const modifyStart =
          prevRange &&
          (range.compareBoundaryPoints(Range.END_TO_END, prevRange) === 0 ||
            range.compareBoundaryPoints(Range.START_TO_END, prevRange) === 0);
        let anchor = modifyStart ? range.startContainer : range.endContainer;
        if (anchor.nodeType === Node.TEXT_NODE) {
          anchor = anchor.parentNode;
        }
        if (!modifyStart && range.endOffset === 0) {
          do {
            while (!anchor.previousSibling) {
              anchor = anchor.parentNode;
            }
            anchor = anchor.previousSibling;
          } while (!anchor.childNodes.length);
        }

        const parentTextLayer = anchor.parentElement?.closest(".textLayer");
        const endDiv = textLayers.get(parentTextLayer);
        if (endDiv) {
          endDiv.style.width = parentTextLayer.style.width;
          endDiv.style.height = parentTextLayer.style.height;
          anchor.parentElement.insertBefore(
            endDiv,
            modifyStart ? anchor : anchor.nextSibling,
          );
        }

        prevRange = range.cloneRange();
      },
      { signal },
    );
  };

  const renderPage = async () => {
    if (!pdfDoc) return;

    const page = await pdfDoc.getPage(pageNumber);
    const viewport = page.getViewport({ scale, rotation });
    console.log("viewport", viewport);
    setViewport(viewport);

    // Render canvas

    const divWrapper = divRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    divWrapper.style.height = `${viewport.height}px`;
    divWrapper.style.width = `${viewport.width}px`;

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };

    await page.render(renderContext).promise;

    const textLayerDiv = textLayerRef.current;
    textLayerDiv.style.setProperty("--total-scale-factor", scale.toString());
    textLayerDiv.innerHTML = ""; // Clear previous content

    const textLayer = new TextLayer({
      textContentSource: page.streamTextContent({
        includeMarkedContent: true,
        disableNormalization: true,
      }),
      viewport: viewport,
      container: textLayerRef.current,
    });
    await textLayer.render();
    textLayerDiv.style.height = `${viewport.height}px`;
    textLayerDiv.style.width = `${viewport.width}px`;
    textLayerDiv.style.pointerEvents = "all";

    const endOfContent = document.createElement("div");
    endOfContent.className = "endOfContent";
    textLayerDiv.append(endOfContent);

    highlightText(textLayerDiv, searchString);

    bindMouse(endOfContent);
  };

  const handlePageClick = (e: React.MouseEvent<HTMLElement>) => {
    if (onClick) {
      onClick(pageNumber, e, viewport);
    }
  };

  const highlightText = (textLayerDiv, term) => {
    if (!term) return;

    const regex = new RegExp(`(${term})`, "gi");
    const pageMatches = [];

    const spans = textLayerDiv.querySelectorAll("span");
    spans.forEach((span) => {
      const originalText = span.textContent;
      if (!originalText || !regex.test(originalText)) return;

      const highlightedHTML = originalText.replace(
        regex,
        `<span class="highlight">$1</span>`,
      );

      span.innerHTML = highlightedHTML;

      // Add all new highlight spans to matches
      const highlights = span.querySelectorAll(".highlight");
      highlights.forEach((el) => {
        pageMatches.push({ element: el, pageNumber });
      });
    });

    // Append to global matches list
    setMatches((prev) => [...prev, ...pageMatches]);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);

        onVisibleChanged(pageNumber, entry.isIntersecting);
      },
      { root: null, threshold: 0.3 }, // Observe when at least 10% of the div is visible
    );

    if (divRef.current) {
      observer.observe(divRef.current);
    }

    return () => {
      if (divRef.current) {
        observer.unobserve(divRef.current);
      }
    };
  }, []);

  useEffect(() => {
    renderPage();
  }, [pdfDoc, scale, rotation, searchString]);

  return (
    <div
      ref={divRef}
      className={styles.page}
      onClick={(e) => handlePageClick(e)}
    >
      <canvas ref={canvasRef} style={{ border: "1px solid #ccc" }} />
      <div
        ref={textLayerRef}
        className="textLayer"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          zIndex: 10,
          pointerEvents: `${PDF.drawLine || PDF.drawPin ? "all" : "none"}`,
        }}
        className={styles.canvas}
      >
        {/* {!sizes.isDraging && ( */}
        {viewport && (
          <Annotations
            documentID={documentId}
            refWrapper={divRef}
            setDrawLine={() => setDrawLine(false)}
            setDrawPin={() => setDrawPin(false)}
            drawLine={PDF.drawLine}
            isRightScreen={false}
            drawPin={PDF.drawPin}
            drawHighlight={PDF.drawHighlight}
            zoom={PDF.zoom}
            page={pageNumber}
            viewport={viewport}
            onAnnotationClick={onAnnotationClick}
            onAnnotationCreated={onAnnotationCreated}
          />
        )}
      </div>

      {signatureField !== null && signatureField.page === pageNumber && (
        <div
          style={{
            zIndex: 10,
            pointerEvents: `all`,
          }}
          className={styles.canvas}
        >
          <Draggable
            scale={scale}
            key={signatureField.id}
            defaultPosition={{
              x: signatureField.x,
              y: signatureField.y,
            }}
            position={{ x: signatureField.x, y: signatureField.y }}
            onStop={(e, data) =>
              updateSignatureField(signatureField.id, { x: data.x, y: data.y })
            }
            defaultClassName={styles.draggable}
            cancel=".react-resizable-handle"
          >
            <ResizableBox
              width={signatureField.width}
              height={signatureField.height}
              axis="both"
              minConstraints={[188 * scale * 0.7, 52 * scale * 0.7]}
              maxConstraints={[188 * scale * 3, 52 * scale * 3]}
              onResizeStart={(e) => e.preventDefault()}
              onResizeStop={(e, { size }) =>
                updateSignatureField(signatureField.id, {
                  width: size.width,
                  height: size.height,
                })
              }
            >
              <div className={styles.signaturePlaceholder}>
                <div className={styles.logo}>
                  <SignatureIcon></SignatureIcon>
                </div>

                <div className={styles.info}>
                  <AutoTextSize mode="oneline" minFontSizePx={4}>
                    <p>
                      <b>Podpisano elektronicznie</b>
                    </p>
                    <p> {user?.profile?.name}</p>
                    <p>{moment().format("HH:mm DD.MM.yyyy")}</p>
                  </AutoTextSize>
                </div>
                <div className={styles.close}>
                  <Tooltip title={t("common.delete")}>
                    <div>
                      <CloseIcon
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemoveSignatureField();
                        }}
                      ></CloseIcon>
                    </div>
                  </Tooltip>
                </div>
              </div>
            </ResizableBox>
          </Draggable>
        </div>
      )}
      {/* )} */}
    </div>
  );
};

const PDFViewer: FC<Props> = ({
  embededCommentsWindow = true,
  file,
  sourceFile,
  showHeader = true,
  inModal = false,
  title,
  showSignaturePanel = true,
  showSignButton = false,
  onSign,
  showCloseButton = true,
  onCloseButton,
  setShowComments,
  docId,
  headerClassName,
  handleOpenInNewTab,
  onlyPDFButtons,
  handleAddToDossier,
  handleAddToCase,
  handleAddToRegistry,
  fileName: ovveriddeFileName,
  signPositioningMode = false,
  signPositioningHint,
  onConfirmSignaturePosition,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const PDFId = queryParams.get("pdf");
  const [showCommentsInternal, setShowCommentsInternal] =
    useState<boolean>(false);
  const [activeAnnotation, setActiveAnnotation] = useState<IAnnotation>(null);

  useContext(LayoutContext);
  const documentID =
    docId ??
    `${PDFId?.split(":")[0]}:${PDFId?.split(":")[3]}:${PDFId?.split(":")[2]}`;

  const splittedPDFId = PDFId?.split(":");

  const packId = splittedPDFId ? splittedPDFId[0] : null;
  const fileName =
    ovveriddeFileName ?? (splittedPDFId ? splittedPDFId[1] : null);
  const fileUri = splittedPDFId ? splittedPDFId[2] : null;
  const fileKind = splittedPDFId ? splittedPDFId[3] : null;

  const ref = useRef<HTMLDivElement>(null);

  const { PDF: pdf, setPDF } = useContext(PDFContext);
  const { cert, setCert } = useContext(CertContext);

  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy>(null);
  const [pages, setPages] = useState(0);

  const [pageNumber, setPageNumber] = useState(0);
  const isFirstPage = pageNumber === 1;
  const isLastPage = pageNumber === pages;

  useContext(CPluginContext);
  const { openEditor } = useDocumentEditor();
  const [visiblePages] = useState<boolean[]>([]);

  usePDF();

  const pageRefs = useRef({});

  const goToPreviousPage = () => {
    if (!isFirstPage) setPageNumber(pageNumber - 1);
    pageRefs.current[pageNumber - 1].scrollIntoView({ behavior: "smooth" });
  };

  const goToNextPage = () => {
    if (!isLastPage) setPageNumber(pageNumber + 1);
    pageRefs.current[pageNumber + 1].scrollIntoView({ behavior: "smooth" });
  };

  const goToFirstPage = () => {
    setPageNumber(1);
    pageRefs.current[1].scrollIntoView({ behavior: "instant" });
  };

  const goToLastPage = () => {
    setPageNumber(pages);
    pageRefs.current[pages].scrollIntoView({ behavior: "instant" });
  };

  const [, setDisableScroll] = useState(false);

  const [isSignature, setIsSignature] = useState(false);
  const [selectionChangeAbortController, setSelectionChangeAbortController] =
    useState<AbortController>(null);

  const [signatureField, setSignatureField] = useState<ISignatureField>(null);
  const [searchString, setSearchString] = useState<string>(null);
  const [matches, setMatches] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  const loadPDF = async (forcedBlob?: Blob) => {
    console.info("Load PDF");
    if ((forcedBlob ?? file) instanceof Blob) {
      const loadingTask = getDocument(await (forcedBlob ?? file).arrayBuffer());
      const pdfd = await loadingTask.promise;
      setPdfDoc(pdfd);
      setPages(pdfd.numPages);

      setPDF({ ...pdf, drawPin: false, drawLine: false, drawHighlight: false });

      const metadata = await pdfd.getMetadata();

      if (metadata?.info?.IsSignaturesPresent) {
        setIsSignature(true);
      } else {
        setIsSignature(false);
      }
    }
  };
  const handlePageVisibleChanged = (pageNumber: number, visible: boolean) => {
    visiblePages[pageNumber] = visible;
    for (let i = 1; i <= pages; i++) {
      if (visiblePages[i]) {
        setPageNumber(i);
        break;
      }
    }
  };

  const { mutate: deleteAnnotation } = annotationService.useDeleteAnnotation();
  const handleDeleteAnnotation = (
    annotation: IAnnotation,
    onSuccess?: () => void,
  ) => {
    deleteAnnotation(
      {
        AnnotationId: annotation.Id,
      },
      {
        onSuccess: () => {
          onSuccess?.();
        },
      },
    );
  };

  const addSignatureField = (
    page: number,
    e: React.MouseEvent<HTMLElement>,
    viewport: PageViewport,
  ) => {
    if (!cert?.id.length && !signPositioningMode) return;
    if (signatureField) return;
    console.log("Adding signature field", pdf.zoom, pageRefs);
    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setSignatureField({
      id: randomString(),
      x,
      y,
      width: 188 * pdf.zoom,
      height: 52 * pdf.zoom,
      zoom: pdf.zoom,
      page: page,
      pwidth: viewport.width,
      pheight: viewport.height,
    });
  };

  const refWrapper = useRef(null);

  const updateSignatureField = (id, newProps) => {
    if (!signatureField) return;

    const newF = { ...signatureField, ...newProps };

    setSignatureField(newF);
  };

  const handleRemoveSignatureField = () => {
    setSignatureField(null);
  };

  const handleDownload = () => {
    if (file instanceof Blob) {
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = replaceFileExtension(fileName, ".pdf");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleEdit = () => {
    const kind = Number(fileKind);
    if (kind === ItemKind.CaseFile || kind === ItemKind.EditorDocument) {
      openEditor({ Id: packId, Kind: kind });
    }
  };

  const handleAnnotationClick = (annotation: IAnnotation) => {
    if (embededCommentsWindow) {
      setShowCommentsInternal(true);
    }

    setActiveAnnotation(annotation);
  };

  const handleAnnotationCreated = (annotation: IAnnotation) => {
    if (embededCommentsWindow) {
      setShowCommentsInternal(true);
    }

    setActiveAnnotation(annotation);
  };

  useEffect(() => {
    if (!signatureField) return;

    const f = signatureField;

    if (pdf.zoom !== f.zoom) {
      const ratio = pdf.zoom / f.zoom;
      console.log("Old X", f.x, f.x * ratio);
      f.x = f.x * ratio;
      f.y = f.y * ratio;
      f.width = f.width * ratio;
      f.height = f.height * ratio;
      f.zoom = pdf.zoom;

      setSignatureField(f);
    }
  }, [pdf.zoom]);

  useEffect(() => {
    loadPDF();
  }, [file]);

  const scrollToMatch = (index) => {
    if (!matches.length) return;
    const { element } = matches[index];
    element.scrollIntoView({ behavior: "smooth", block: "center" });

    // Optional: visually mark current match
    matches.forEach((m, i) => {
      m.element.style.backgroundColor = i === index ? "#feff0360" : "#b400aa40";
    });
  };

  const nextMatch = () => {
    if (currentMatchIndex < matches.length - 1) {
      scrollToMatch(currentMatchIndex + 1);
      setCurrentMatchIndex(currentMatchIndex + 1);
    }
  };

  const prevMatch = () => {
    if (currentMatchIndex > 0) {
      scrollToMatch(currentMatchIndex - 1);
      setCurrentMatchIndex(currentMatchIndex - 1);
    }
  };

  const handleSigned = (newFile: Blob) => {
    console.log("Handle signed");
    loadPDF(newFile);
    setCert(null);
  };

  useEffect(() => {
    if (matches?.length > 0) {
      scrollToMatch(0);
      setCurrentMatchIndex(0);
    }
  }, [matches]);

  return (
    <div
      onMouseEnter={() => {
        setDisableScroll(true);
      }}
      onMouseLeave={() => {
        setDisableScroll(false);
      }}
      className={classNames(styles.wrapper, inModal ? styles.inModal : null)}
    >
      {showHeader && (
        <PDFVieverHeader
          file={file}
          title={title ?? fileName}
          setShowComments={() => {
            if (embededCommentsWindow) {
              setShowCommentsInternal(true);
            } else {
              setShowComments();
            }
          }}
          pdfRef={ref}
          showCloseButton={showCloseButton}
          onCloseButton={onCloseButton}
          pages={pages}
          currentPage={pageNumber}
          canGoNextPage={!isFirstPage}
          canGoPrevPage={!isLastPage}
          onGotoNextPage={() => goToNextPage()}
          onGotoPrevPage={() => goToPreviousPage()}
          inModal={inModal}
          headerClassName={headerClassName}
          onlyPDFButtons={onlyPDFButtons}
          handleOpenInNewTab={handleOpenInNewTab}
          handleDownload={handleDownload}
          handleEdit={handleEdit}
          handleAddDossier={handleAddToDossier}
          handleAddCase={handleAddToCase}
          handleAddToRegistry={handleAddToRegistry}
          searchString={searchString}
          setSearchString={(s) => {
            setMatches([]);
            setCurrentMatchIndex(0);
            setSearchString(s);
          }}
          matches={matches}
          currentMatchIndex={currentMatchIndex}
          nextMatch={nextMatch}
          prevMatch={prevMatch}
          sign={!!cert?.id}
          showSignButton={showSignButton}
          onSign={
            onSign ??
            (() =>
              setCert({
                id: fileName || title || "document",
                name: fileName ?? title ?? "",
              }))
          }
          onGotoFirstPage={() => goToFirstPage()}
          onGotoLastPage={() => goToLastPage()}
        />
      )}

      {(pdf.drawPin || pdf.drawLine || pdf.drawHighlight) && (
        <div className={styles.bar}>
          <div className={styles.barWrapper}>
            <div className={styles.text}>
              <b>{t("pdf.addingAnnotation")}</b>
              {t("pdf.annotationClickHint")}
            </div>
            <div className={styles.buttons}>
              <Button
                text={t("pdf.closeAnnotationMode")}
                onClick={() => {
                  if (
                    activeAnnotation &&
                    activeAnnotation.Comments.length === 0
                  ) {
                    handleDeleteAnnotation(activeAnnotation, () => {
                      const annotations = [...pdf.annotations];

                      setPDF({
                        ...pdf,
                        drawLine: false,
                        drawPin: false,
                        drawHighlight: false,
                        annotations: annotations.filter(
                          (a) => a.Id !== activeAnnotation.Id,
                        ),
                      });
                    });
                  } else {
                    setPDF({
                      ...pdf,
                      drawLine: false,
                      drawPin: false,
                      drawHighlight: false,
                    });
                  }
                  setActiveAnnotation(null);
                  setShowCommentsInternal(false);
                }}
              ></Button>
            </div>
          </div>
        </div>
      )}

      {signPositioningMode && (
        <div
          className={classNames(styles.descr, inModal ? styles.inModal : null)}
        >
          <div className={styles.signPositioning}>
            <div className={styles.signPositioningHint}>
              {signPositioningHint ??
                "Kliknij na piśmie, aby ustawić znacznik podpisu, dostosuj jego położenie i rozmiar, a następnie zatwierdź."}
            </div>
            <Button
              className={styles.signPositioningBtn}
              text="Zatwierdź pozycję podpisu"
              onClick={() => onConfirmSignaturePosition?.(signatureField)}
            />
          </div>
        </div>
      )}
      {isSignature && showSignaturePanel && !cert?.id.length && !signPositioningMode && (
        <div
          className={classNames(styles.descr, inModal ? styles.inModal : null)}
        >
          <SignatureLabel content={file} />
        </div>
      )}
      {!!cert?.id.length && (
        <div className={styles.descr}>
          <CertificateApi
            itemId={packId}
            itemKind={Number(fileKind)}
            fileName={fileName}
            attachmentUri={fileUri}
            file={file}
            originalFile={sourceFile}
            signatureField={signatureField}
            onSigned={handleSigned}
            pageHeight={
              "1" in pageRefs.current ? pageRefs.current[1].clientHeight : -1
            }
            pageWidth={
              "1" in pageRefs.current ? pageRefs.current[1].clientWidth : -1
            }
          />
        </div>
      )}

      <div className={styles.pagesWrapper} ref={ref}>
        {Array(...Array(pages))
          .map((x, i) => i + 1)
          .map((page, idx) => (
            <div
              ref={(el) => {
                pageRefs.current[page] = el;
              }}
              key={idx}
              className={styles.pageWrapper}
            >
              <PDFPage2
                pdf={pdf}
                setPDF={setPDF}
                scale={pdf.zoom}
                rotation={pdf.rotate}
                pageNumber={page}
                onVisibleChanged={handlePageVisibleChanged}
                onClick={addSignatureField}
                textLayers={textLayers}
                pdfDoc={pdfDoc}
                selectionChangeAbortController={selectionChangeAbortController}
                setSelectionChangeAbortController={
                  setSelectionChangeAbortController
                }
                documentId={documentID}
                onAnnotationClick={handleAnnotationClick}
                onAnnotationCreated={handleAnnotationCreated}
                activeAnnotation={activeAnnotation}
                setActiveAnnotation={setActiveAnnotation}
                searchString={searchString}
                setMatches={setMatches}
                signatureField={signatureField}
                updateSignatureField={updateSignatureField}
                handleRemoveSignatureField={handleRemoveSignatureField}
                user={auth?.user}
              ></PDFPage2>
            </div>
          ))}
      </div>

      {showCommentsInternal && (
        <ModalLayout
          name="createComment"
          dock={DockPosition.DISABLED}
          off={true}
          customX={8}
          customY={0}
          ignore
          isAnimation
          refWrapper={refWrapper}
          fullHeight={true}
        >
          <Comments
            toggle={() => {
              setShowCommentsInternal(false);
              setActiveAnnotation(null);
            }}
            activeAnnotation={activeAnnotation}
            setActiveAnnotation={setActiveAnnotation}
          />
        </ModalLayout>
      )}
    </div>
  );
};

export default PDFViewer;
